import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import {
  Lock,
  Unlock,
  Plus,
  Search,
  Eye,
  EyeOff,
  Copy,
  Pencil,
  Trash2,
  KeyRound,
  ShieldCheck,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/vault`;
const AUTO_LOCK_MS = 15 * 60 * 1000; // 15 minutes idle
const DEFAULT_CATEGORIES = ["Portal", "API Key", "Personal", "Bank", "Other"];

const CATEGORY_COLORS = {
  "Portal": "bg-blue-100 text-blue-700 border-blue-200",
  "API Key": "bg-purple-100 text-purple-700 border-purple-200",
  "Personal": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Bank": "bg-amber-100 text-amber-700 border-amber-200",
  "Other": "bg-slate-100 text-slate-700 border-slate-200",
};

const categoryClass = (name) =>
  CATEGORY_COLORS[name] || "bg-slate-100 text-slate-700 border-slate-200";

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

export default function PasswordVaultPage() {
  // 'checking' | 'setup' | 'locked' | 'unlocked'
  const [stage, setStage] = useState("checking");
  const [masterPw, setMasterPw] = useState("");
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [revealed, setRevealed] = useState({}); // {id: true}
  const [editing, setEditing] = useState(null); // entry being edited or null (create) or "close"
  const [showChangeMaster, setShowChangeMaster] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const idleTimerRef = useRef(null);

  const lock = useCallback(() => {
    setMasterPw("");
    setEntries([]);
    setRevealed({});
    setStage("locked");
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
  }, []);

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      toast.info("Vault auto-locked due to inactivity");
      lock();
    }, AUTO_LOCK_MS);
  }, [lock]);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/status`, { headers: authHeader() });
      if (res.status === 403) {
        setStage("forbidden");
        return;
      }
      const data = await res.json();
      setStage(data.is_setup ? "locked" : "setup");
    } catch {
      setStage("locked");
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [fetchStatus]);

  useEffect(() => {
    if (stage !== "unlocked") return;
    // wire idle detection
    const events = ["mousemove", "keydown", "click", "touchstart"];
    events.forEach((e) => window.addEventListener(e, resetIdleTimer));
    resetIdleTimer();
    return () => {
      events.forEach((e) => window.removeEventListener(e, resetIdleTimer));
    };
  }, [stage, resetIdleTimer]);

  const loadEntries = async (pw) => {
    setLoading(true);
    try {
      const [entriesRes, catsRes] = await Promise.all([
        fetch(`${API}/entries`, {
          headers: { ...authHeader(), "X-Master-Password": pw },
        }),
        fetch(`${API}/categories`, { headers: authHeader() }),
      ]);
      if (!entriesRes.ok) throw new Error((await entriesRes.json()).detail || "Failed to load");
      const data = await entriesRes.json();
      setEntries(data.entries || []);
      if (catsRes.ok) {
        const cd = await catsRes.json();
        setCategories(cd.categories || []);
      }
    } catch (e) {
      toast.error(e.message);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (pw, confirmPw) => {
    if (pw.length < 8) return toast.error("Master password must be at least 8 characters");
    if (pw !== confirmPw) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      const res = await fetch(`${API}/setup`, {
        method: "POST",
        headers: { ...authHeader(), "Content-Type": "application/json" },
        body: JSON.stringify({ master_password: pw }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Setup failed");
      toast.success("Vault created. Unlock it with your master password.");
      setStage("locked");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = async (pw) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/verify`, {
        method: "POST",
        headers: { ...authHeader(), "Content-Type": "application/json" },
        body: JSON.stringify({ master_password: pw }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Invalid master password");
      setMasterPw(pw);
      await loadEntries(pw);
      setStage("unlocked");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEntry = async (payload, id) => {
    setLoading(true);
    try {
      const url = id ? `${API}/entries/${id}` : `${API}/entries`;
      const res = await fetch(url, {
        method: id ? "PUT" : "POST",
        headers: {
          ...authHeader(),
          "Content-Type": "application/json",
          "X-Master-Password": masterPw,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Save failed");
      toast.success(id ? "Entry updated" : "Entry saved");
      setEditing(null);
      await loadEntries(masterPw);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API}/entries/${id}`, {
        method: "DELETE",
        headers: { ...authHeader(), "X-Master-Password": masterPw },
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Delete failed");
      toast.success("Entry deleted");
      setDeleteTarget(null);
      await loadEntries(masterPw);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleChangeMaster = async (curr, next, confirm) => {
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirm) return toast.error("New passwords do not match");
    setLoading(true);
    try {
      const res = await fetch(`${API}/change-master`, {
        method: "POST",
        headers: { ...authHeader(), "Content-Type": "application/json" },
        body: JSON.stringify({ current_password: curr, new_password: next }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Change failed");
      const data = await res.json();
      toast.success(`Master password changed. ${data.re_encrypted} entries re-encrypted.`);
      setShowChangeMaster(false);
      setMasterPw(next);
      await loadEntries(next);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Clipboard blocked by browser");
    }
  };

  const filteredEntries = entries.filter((e) => {
    if (activeCategory !== "All" && (e.category || "Other") !== activeCategory) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      e.service_name.toLowerCase().includes(q) ||
      e.username.toLowerCase().includes(q) ||
      (e.notes || "").toLowerCase().includes(q) ||
      (e.category || "").toLowerCase().includes(q)
    );
  });

  // ---- Render ----

  if (stage === "checking") {
    return (
      <div className="flex items-center justify-center h-96 text-slate-500" data-testid="vault-loading">
        <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading vault…
      </div>
    );
  }

  if (stage === "forbidden") {
    return (
      <div className="max-w-md mx-auto mt-16 text-center p-8 bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="vault-forbidden">
        <Lock className="w-10 h-10 text-slate-400 mx-auto mb-3" />
        <h2 className="text-xl font-semibold text-slate-800">Restricted</h2>
        <p className="text-slate-500 text-sm mt-2">The password vault is available to Super Admins only.</p>
      </div>
    );
  }

  if (stage === "setup") {
    return <VaultSetupCard loading={loading} onSetup={handleSetup} />;
  }

  if (stage === "locked") {
    return (
      <VaultUnlockCard
        loading={loading}
        onUnlock={handleUnlock}
      />
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="vault-unlocked">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-emerald-600" />
            <h1 className="text-2xl font-bold text-slate-900">Password Vault</h1>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {entries.length} entr{entries.length === 1 ? "y" : "ies"} · encrypted at rest with your master password
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowChangeMaster(true)} data-testid="vault-change-master-btn">
            <KeyRound className="w-4 h-4 mr-2" /> Change master
          </Button>
          <Button variant="outline" onClick={lock} data-testid="vault-lock-btn">
            <Lock className="w-4 h-4 mr-2" /> Lock
          </Button>
          <Button onClick={() => setEditing({})} data-testid="vault-add-entry-btn">
            <Plus className="w-4 h-4 mr-2" /> Add entry
          </Button>
        </div>
      </div>

      {/* Category filter chips */}
      {categories.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="vault-category-filters">
          <button
            onClick={() => setActiveCategory("All")}
            className={`px-3 py-1 text-xs rounded-full border transition ${
              activeCategory === "All"
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
            }`}
            data-testid="vault-cat-chip-all"
          >
            All · {entries.length}
          </button>
          {categories.map((c) => (
            <button
              key={c.name}
              onClick={() => setActiveCategory(c.name)}
              className={`px-3 py-1 text-xs rounded-full border transition ${
                activeCategory === c.name
                  ? "bg-slate-900 text-white border-slate-900"
                  : `${categoryClass(c.name)} hover:opacity-80`
              }`}
              data-testid={`vault-cat-chip-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {c.name} · {c.count}
            </button>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search service, username or notes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="vault-search-input"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Service</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Username</TableHead>
              <TableHead>Password</TableHead>
              <TableHead>Notes</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredEntries.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-slate-400 py-10" data-testid="vault-empty">
                  {entries.length === 0 ? "No entries yet. Click 'Add entry' to save your first credential." : "No matches for that search."}
                </TableCell>
              </TableRow>
            )}
            {filteredEntries.map((e) => (
              <TableRow key={e.id} data-testid={`vault-row-${e.id}`}>
                <TableCell className="font-medium text-slate-800">{e.service_name}</TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={`${categoryClass(e.category || "Other")} font-normal`}
                    data-testid={`vault-cat-badge-${e.id}`}
                  >
                    {e.category || "Other"}
                  </Badge>
                </TableCell>
                <TableCell className="text-slate-700">
                  <div className="flex items-center gap-1">
                    <span className="truncate max-w-[180px]">{e.username || "—"}</span>
                    {e.username && (
                      <button onClick={() => copyText(e.username, "Username")} className="text-slate-400 hover:text-slate-700" title="Copy username" data-testid={`vault-copy-user-${e.id}`}>
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 font-mono text-sm">
                    <span className="truncate max-w-[160px]">
                      {revealed[e.id] ? e.password : "••••••••••"}
                    </span>
                    <button
                      onClick={() => setRevealed((r) => ({ ...r, [e.id]: !r[e.id] }))}
                      className="text-slate-400 hover:text-slate-700"
                      title={revealed[e.id] ? "Hide" : "Reveal"}
                      data-testid={`vault-toggle-pw-${e.id}`}
                    >
                      {revealed[e.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      onClick={() => copyText(e.password, "Password")}
                      className="text-slate-400 hover:text-slate-700"
                      title="Copy password"
                      data-testid={`vault-copy-pw-${e.id}`}
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </TableCell>
                <TableCell className="max-w-[260px] truncate text-slate-600 text-sm" title={e.notes}>
                  {e.notes || "—"}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(e)} data-testid={`vault-edit-${e.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteTarget(e)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`vault-delete-${e.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Entry Modal */}
      {editing !== null && (
        <EntryModal
          entry={editing}
          onClose={() => setEditing(null)}
          onSave={handleSaveEntry}
          loading={loading}
        />
      )}

      {/* Change Master Modal */}
      {showChangeMaster && (
        <ChangeMasterModal
          onClose={() => setShowChangeMaster(false)}
          onSave={handleChangeMaster}
          loading={loading}
        />
      )}

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium">{deleteTarget?.service_name}</span> will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="vault-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && handleDelete(deleteTarget.id)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="vault-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ------------ Sub-components ------------

function VaultSetupCard({ loading, onSetup }) {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  return (
    <div className="max-w-md mx-auto mt-16" data-testid="vault-setup">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="w-6 h-6 text-indigo-600" />
          <h1 className="text-xl font-semibold text-slate-900">Create your vault</h1>
        </div>
        <p className="text-sm text-slate-500 mb-6">
          Pick a strong master password. It never leaves your device unencrypted, and it cannot be recovered if lost —
          the vault is encrypted with a key derived from this password.
        </p>
        <div className="space-y-4">
          <div>
            <Label>Master password</Label>
            <Input
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="At least 8 characters"
              data-testid="vault-setup-pw"
            />
          </div>
          <div>
            <Label>Confirm master password</Label>
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="vault-setup-confirm"
            />
          </div>
          <Button
            className="w-full"
            disabled={loading}
            onClick={() => onSetup(pw, confirm)}
            data-testid="vault-setup-submit"
          >
            {loading ? "Creating…" : "Create vault"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function VaultUnlockCard({ loading, onUnlock }) {
  const [pw, setPw] = useState("");
  return (
    <div className="max-w-md mx-auto mt-16" data-testid="vault-locked">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
        <div className="flex items-center gap-2 mb-4">
          <Lock className="w-6 h-6 text-slate-700" />
          <h1 className="text-xl font-semibold text-slate-900">Vault locked</h1>
        </div>
        <p className="text-sm text-slate-500 mb-6">Enter your master password to unlock.</p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onUnlock(pw);
          }}
          className="space-y-4"
        >
          <div>
            <Label>Master password</Label>
            <Input
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoFocus
              data-testid="vault-unlock-pw"
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading || !pw} data-testid="vault-unlock-submit">
            <Unlock className="w-4 h-4 mr-2" />
            {loading ? "Unlocking…" : "Unlock vault"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function EntryModal({ entry, onClose, onSave, loading }) {
  const isEdit = !!entry?.id;
  const [service, setService] = useState(entry?.service_name || "");
  const [username, setUsername] = useState(entry?.username || "");
  const [password, setPassword] = useState(entry?.password || "");
  const [notes, setNotes] = useState(entry?.notes || "");
  const [category, setCategory] = useState(entry?.category || "Other");
  const [showPw, setShowPw] = useState(false);

  const submit = () => {
    if (!service.trim()) return toast.error("Service name is required");
    const payload = { service_name: service, username, notes, category };
    // On edit, only include password if changed (avoid clobbering with empty)
    if (!isEdit || password !== (entry?.password || "")) {
      payload.password = password;
    }
    onSave(payload, entry?.id);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="vault-entry-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit entry" : "Add new entry"}</DialogTitle>
          <DialogDescription>Password will be encrypted before it hits the database.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Service name *</Label>
            <Input
              value={service}
              onChange={(e) => setService(e.target.value)}
              placeholder="e.g. India Post Booking Portal"
              data-testid="vault-form-service"
            />
          </div>
          <div>
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="vault-form-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DEFAULT_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`vault-form-cat-opt-${c.toLowerCase().replace(/\s+/g, "-")}`}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Username / email</Label>
            <Input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Optional"
              data-testid="vault-form-username"
            />
          </div>
          <div>
            <Label>Password</Label>
            <div className="relative">
              <Input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="vault-form-password"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional context, security questions, 2FA backup codes…"
              rows={3}
              data-testid="vault-form-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="vault-form-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={loading} data-testid="vault-form-save">
            {loading ? "Saving…" : isEdit ? "Save changes" : "Save entry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChangeMasterModal({ onClose, onSave, loading }) {
  const [curr, setCurr] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="vault-change-master-modal">
        <DialogHeader>
          <DialogTitle>Change master password</DialogTitle>
          <DialogDescription>
            All existing entries will be re-encrypted with the new key. Make sure you remember the new password —
            it cannot be recovered.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Current master password</Label>
            <Input type="password" value={curr} onChange={(e) => setCurr(e.target.value)} data-testid="cm-current" />
          </div>
          <div>
            <Label>New master password</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="cm-new" />
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="cm-confirm"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="cm-cancel">
            Cancel
          </Button>
          <Button onClick={() => onSave(curr, next, confirm)} disabled={loading} data-testid="cm-save">
            {loading ? "Changing…" : "Change password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
